import { useCallback } from "react";
import { ElementPosition } from "../types/types";
import { Container } from "pixi.js";

// Module scope variable to control text-to-texture rendering for performance
// Set to true to cache text as texture (better performance for static text)
const ENABLE_TEXT_TO_TEXTURE = true;

// Helper function to create a canvas-based text measurement
const measureTextWidth = (text: string, fontSize: number): number => {
  // Create a temporary canvas to measure text width accurately
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return text.length * fontSize * 0.5; // Fallback

  // Use device pixel ratio for more accurate measurements on Retina displays
  const dpr = window.devicePixelRatio || 1;
  const scaledFontSize = fontSize * dpr;

  ctx.font = `bold ${scaledFontSize}px Arial`; // Match the style used in Pixi
  const metrics = ctx.measureText(text);
  return metrics.width / dpr; // Convert back to logical pixels
};

// Helper function to truncate text with ellipsis using accurate measurements
const truncateTextWithEllipsis = (
  text: string,
  fontSize: number,
  maxWidth: number
): string => {
  if (!maxWidth || maxWidth <= 0) return text;

  // Measure the full text width
  const fullWidth = measureTextWidth(text, fontSize);
  if (fullWidth <= maxWidth) {
    return text;
  }

  // Measure ellipsis width
  const ellipsisWidth = measureTextWidth("...", fontSize);
  const availableWidth = maxWidth - ellipsisWidth;

  if (availableWidth <= 0) return "...";

  // Binary search to find the optimal truncation point
  let left = 0;
  let right = text.length;
  let bestFit = "";

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const candidate = text.substring(0, mid);
    const candidateWidth = measureTextWidth(candidate, fontSize);

    if (candidateWidth <= availableWidth) {
      bestFit = candidate;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  // If we found a good fit, try to improve it by finding word boundaries
  if (bestFit) {
    const lastSpaceIndex = bestFit.lastIndexOf(" ");
    if (lastSpaceIndex > 0) {
      const wordBoundaryCandidate = bestFit.substring(0, lastSpaceIndex);
      const wordBoundaryWidth = measureTextWidth(
        wordBoundaryCandidate,
        fontSize
      );

      // Use word boundary if it's not too much shorter (don't lose more than 30% of available space)
      if (wordBoundaryWidth >= availableWidth * 0.7) {
        bestFit = wordBoundaryCandidate;
      }
    }
  }

  return bestFit.trim() + "...";
};

export function TileTitle({
  text,
  fontSize,
  color,
  position,
  maxWidth,
}: {
  text: string;
  fontSize: number;
  color: string | number;
  position: ElementPosition;
  maxWidth?: number;
}) {
  // Memoized ref callback to avoid re-creating the function on every render
  // This prevents unnecessary cacheAsTexture toggling
  const containerRef = useCallback((instance: Container) => {
    // Cache the container as texture (PixiJS v8+) for better performance
    // This renders the container and its children to a texture, reducing draw calls
    // Higher resolution ensures crisp text when cached as texture
    if (instance && ENABLE_TEXT_TO_TEXTURE) {
      instance.cacheAsTexture({
        resolution: 2,
        antialias: true,
      });
    }
  }, []); // Empty deps since ENABLE_TEXT_TO_TEXTURE is a module constant

  try {
    const displayText = maxWidth
      ? truncateTextWithEllipsis(text, fontSize, maxWidth)
      : text;

    return (
      <pixiContainer
        x={position.x}
        y={position.y}
        ref={containerRef}
      >
        <pixiBitmapText
          text={displayText}
          style={{
            fontFamily: "Arial", // Bitmap font name - you'll need to load this font
            fontSize: fontSize,
            fontWeight: "700",
            fill: color,
          }}
          anchor={position.anchor}
        />
      </pixiContainer>
    );
  } catch (error) {
    console.warn("Error rendering TileTitle:", error);
    // Fallback: render nothing if text rendering fails
    return null;
  }
}
